import { createContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import useFetchClientsResults from 'hooks/useFetchClientsResults';

const PROFILE = 'profile';
const ACCESS_TOKEN = 'accessToken';

export function useUserContext(promoPath) {
    const [accessToken, setAccessToken] = useState(localStorage.getItem(ACCESS_TOKEN));
    const [currentUser, _setCurrentUser] = useState(JSON.parse(localStorage.getItem(PROFILE) ?? 'null'));
    const [currentPromo, setCurrentPromo] = useState(null);

    const [loadingProfile, setLoadingProfile] = useState(false);
    const [loadingCurrentPromo, setLoadingCurrentPromo] = useState(true);

    const [error, setError] = useState(null);

    const location = useLocation();
    const navigate = useNavigate();

    const setCurrentUser = (currentUser, token) => {
        if (currentUser && token) {
            localStorage.setItem(ACCESS_TOKEN, token);
            localStorage.setItem(PROFILE, JSON.stringify(currentUser));
        } else {
            localStorage.removeItem(ACCESS_TOKEN);
            localStorage.removeItem(PROFILE);
        }
        _setCurrentUser(currentUser);
        setAccessToken(token);
    };

    useEffect(() => {
        if (promoPath && (!currentPromo || currentPromo.path !== promoPath)) {
            setLoadingCurrentPromo(true);
            axios.get(`${process.env.REACT_APP_API_URL}/promos/${promoPath}`)
                .then((r) => {
                    const currentPromo = r.data;
                    setCurrentPromo(currentPromo);

                    const targetPathname = `/${currentPromo.path}${location.pathname.replace(`/${promoPath}`, '')}`;
                    const needsNav = location.pathname !== targetPathname;
                    if (needsNav) {
                        navigate(
                            { pathname: targetPathname, search: location.search, hash: location.hash },
                            { replace: true }
                        );
                    }
                })
                .catch(() => setError('Não foi possível carregar os dados da promoção.'))
                .finally(() => setLoadingCurrentPromo(false));
        } else if (!promoPath) {
            axios.get(`${process.env.REACT_APP_API_URL}/promos/fallback`)
                .then((r) => {
                    const currentPromo = r.data;
                    setCurrentPromo(currentPromo);

                    const targetPathname = `/${currentPromo.path}${location.pathname.replace(`/${promoPath}`, '')}`;
                    const needsNav = location.pathname !== targetPathname;
                    if (needsNav) {
                        navigate(
                            { pathname: targetPathname, search: location.search, hash: location.hash },
                            { replace: true }
                        );
                    }
                })
                .catch(() => setError('Não foi possível carregar os dados da promoção.'))
                .finally(() => setLoadingCurrentPromo(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPromo, promoPath]);

    useEffect(() => {
        if (!accessToken || !currentPromo) {
            return;
        }
        setLoadingProfile(true);
        axios.get(`${process.env.REACT_APP_API_URL}/client/me?promo_id=${currentPromo.id}`, {
            headers: { 'Authorization': `Bearer ${accessToken}` },
        })
            .then((response) => _setCurrentUser(response.data.client))
            .catch(() => setError('Não foi possível carregar os dados do usuário.'))
            .finally(() => setLoadingProfile(false));
    }, [accessToken, currentPromo]);

    const results = useFetchClientsResults(currentPromo?.id, accessToken);

    return {
        loading: loadingCurrentPromo || loadingProfile,
        error,
        currentUser,
        setCurrentUser,
        currentPromo,
        setCurrentPromo,
        accessToken,
        currentUserResults: results
    };
}

const UserContext = createContext({
    loading: false,
    error: undefined,
    currentUser: undefined,
    accessToken: undefined,
    currentPromo: undefined,
    setCurrentPromo: (_) => undefined,
    setCurrentUser: (_, __) => undefined,
    currentUserResults: {
        results: [],
        hasPendingPrize: false,
        fetch: () => undefined,
    },
});

export default UserContext;
